import {
  captureException as captureDeferredException,
  captureMessage as captureDeferredMessage,
  connectSentry,
  setContext as setDeferredContext,
  setUser as setDeferredUser,
  withScope as withDeferredScope,
  type CaptureExceptionArgs,
  type CaptureMessageArgs,
  type DeferredScope,
  type SentryModule,
} from "@lib/telemetry/sentry";
import type { User } from "@sentry/react";

type ScopeContext = Parameters<DeferredScope["setContext"]>[1];

let initialization: Promise<void> | null = null;
let removeEarlyErrorListeners: (() => void) | null = null;

export function captureException(...args: CaptureExceptionArgs): string {
  return captureDeferredException(...args);
}

export function captureMessage(...args: CaptureMessageArgs): string {
  return captureDeferredMessage(...args);
}

export function setContext(name: string, context: ScopeContext): void {
  setDeferredContext(name, context);
}

export function setUser(user: User | null): void {
  setDeferredUser(user);
}

export function withScope<T>(callback: (scope: DeferredScope) => T): T {
  return withDeferredScope(callback);
}

function installEarlyErrorBuffer(): void {
  if (removeEarlyErrorListeners) return;

  const handleError = (event: ErrorEvent) => {
    captureException(
      event.error ?? new Error(event.message || "Uncaught browser error"),
      {
        extra: {
          earlyError: true,
          filename: event.filename,
          line: event.lineno,
          column: event.colno,
        },
      },
    );
  };
  const handleRejection = (event: PromiseRejectionEvent) => {
    captureException(event.reason, {
      extra: { earlyError: true, source: "unhandledrejection" },
    });
  };

  window.addEventListener("error", handleError);
  window.addEventListener("unhandledrejection", handleRejection);
  removeEarlyErrorListeners = () => {
    window.removeEventListener("error", handleError);
    window.removeEventListener("unhandledrejection", handleRejection);
    removeEarlyErrorListeners = null;
  };
}

/**
 * Installs an early browser-error buffer, then loads and initializes Sentry
 * outside the entry's static import closure. BrowserTracing still backdates
 * its pageload span to performance.timeOrigin when the SDK becomes ready.
 */
export function initializeSentry(
  configure: (sdk: SentryModule) => void | Promise<void>,
  load: () => Promise<SentryModule> = () => import("@sentry/react"),
): Promise<void> {
  if (initialization) return initialization;

  installEarlyErrorBuffer();
  initialization = load()
    .then(async (sdk) => {
      await configure(sdk);
      connectSentry(sdk);
      removeEarlyErrorListeners?.();
    })
    .catch((error: unknown) => {
      console.error("Failed to initialize Sentry", error);
    });

  return initialization;
}
